import { c as _c } from "react/compiler-runtime";
import { IcoBolt24, IcoIdCard24, IcoShield24 } from '@onefootprint/icons';
import type { AdverseMediaListKind, GraphOperationUpdateNode, ModernVerificationCheckConfigBaseLayer, ModernVerificationCheckConfigBusinessAml, ModernVerificationCheckConfigKyb, ModernVerificationCheckConfigKyc, ModernVerificationCheckConfigNeuroId, ModernVerificationCheckConfigSentilink, NodeConfigurationContextVerificationCheck, VerificationCheckDisabledReason } from '@onefootprint/request-types/dashboard';
import { Divider, Radio, Shimmer, Tooltip } from '@onefootprint/ui';
import isEqual from 'lodash/isEqual';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { VerificationCheckNodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import Section from '../../../section';
import UserAmlForm from '../../user-aml-form';
import type { VerificationCheckFormValues } from '../../verification-check.types';
import TooltipToggle from './components/tooltip-toggle';
type VerificationCheckFormProps = {
  node: VerificationCheckNodeProps;
  canEdit: boolean;
  config: NodeConfigurationContextVerificationCheck;
  mutation: ApplyEditMutation;
};

// A Record (not an array) so adding an AdverseMediaListKind variant fails to compile here until it's listed.
const ALL_ADVERSE_MEDIA_CATEGORIES: Record<AdverseMediaListKind, true> = {
  financial_crime: true,
  violent_crime: true,
  sexual_crime: true,
  cyber_crime: true,
  terrorism: true,
  fraud: true,
  narcotics: true,
  general_serious: true,
  general_minor: true
};
const ADVERSE_MEDIA_CATEGORIES = Object.keys(ALL_ADVERSE_MEDIA_CATEGORIES) as AdverseMediaListKind[];

// A null/omitted list means "all categories" server-side, so reflect that as all-selected.
const getAdverseMediaListDefaults = (adverseMedia: boolean, storedLists: AdverseMediaListKind[] | null | undefined): VerificationCheckFormValues['aml']['adverseMediaList'] => {
  const selected = adverseMedia && storedLists == null ? ADVERSE_MEDIA_CATEGORIES : storedLists ?? [];
  const entries = ADVERSE_MEDIA_CATEGORIES.map(category => [category, selected.includes(category)] as [AdverseMediaListKind, boolean]);
  return Object.fromEntries(entries) as VerificationCheckFormValues['aml']['adverseMediaList'];
};
export const VerificationCheckFormInternal = t0 => {
  const $ = _c(107);
  const {
    node,
    canEdit,
    config,
    mutation
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  let baselayerCheck;
  let kybCheck;
  let matchKind;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== node.data.verificationChecks) {
    const amlCheck = node.data.verificationChecks.find(_temp);
    kybCheck = node.data.verificationChecks.find(_temp2);
    baselayerCheck = node.data.verificationChecks.find(_temp3);
    matchKind = amlCheck?.data.matchKind || "fuzzy_low";
    t7 = node.data.verificationChecks.some(_temp4);
    t8 = node.data.verificationChecks.some(_temp5);
    t9 = node.data.verificationChecks.some(_temp6);
    t1 = !!amlCheck;
    t2 = amlCheck?.data.ofac ?? false;
    t3 = amlCheck?.data.pep ?? false;
    t4 = amlCheck?.data.adverseMedia ?? false;
    t5 = amlCheck?.data.continuousMonitoring ?? true;
    t6 = getAdverseMediaListDefaults(amlCheck?.data.adverseMedia ?? false, amlCheck?.data.adverseMediaLists);
    $[0] = node.data.verificationChecks;
    $[1] = baselayerCheck;
    $[2] = kybCheck;
    $[3] = matchKind;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    baselayerCheck = $[1];
    kybCheck = $[2];
    matchKind = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  const t10 = matchKind === "exact_name" || matchKind === "exact_name_and_dob_year" ? "exact" : "fuzzy";
  const t11 = matchKind !== "exact_name" && matchKind !== "exact_name_and_dob_year" ? matchKind : "fuzzy_low";
  const t12 = matchKind === "exact_name" || matchKind === "exact_name_and_dob_year" ? matchKind : "exact_name";
  let t13;
  if ($[13] !== t10 || $[14] !== t11 || $[15] !== t12) {
    t13 = {
      kind: t10,
      fuzzyLevel: t11,
      exactLevel: t12
    };
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t1 || $[18] !== t13 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t14 = {
      enhancedAml: t1,
      ofac: t2,
      pep: t3,
      adverseMedia: t4,
      continuousMonitoring: t5,
      adverseMediaList: t6,
      matchingMethod: t13
    };
    $[17] = t1;
    $[18] = t13;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = !!kybCheck;
  const t16 = kybCheck?.data.einOnly ?? false;
  let t17;
  if ($[25] !== node.data.verificationChecks) {
    t17 = node.data.verificationChecks.some(_temp7);
    $[25] = node.data.verificationChecks;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = !!baselayerCheck;
  const t19 = baselayerCheck?.data.einOnly ?? false;
  let t20;
  if ($[27] !== t14 || $[28] !== t15 || $[29] !== t16 || $[30] !== t17 || $[31] !== t18 || $[32] !== t19 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t20 = {
      kyc: t7,
      sentilink: t8,
      neuroId: t9,
      aml: t14,
      kyb: t15,
      einOnly: t16,
      businessAml: t17,
      baselayer: t18,
      baselayerEinOnly: t19
    };
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const defaultValues = t20;
  const {
    toggle
  } = useDirty();
  let t21;
  if ($[37] !== defaultValues) {
    t21 = {
      defaultValues
    };
    $[37] = defaultValues;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const methods = useForm(t21);
  const {
    control,
    handleSubmit,
    formState
  } = methods;
  let t22;
  if ($[39] !== control) {
    t22 = {
      control,
      name: "aml"
    };
    $[39] = control;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const amlValues = useWatch(t22);
  let t23;
  if ($[41] !== amlValues || $[42] !== defaultValues.aml || $[43] !== formState.isDirty || $[44] !== toggle) {
    t23 = () => {
      const isAmlFormDirty = !isEqual(amlValues, defaultValues.aml);
      toggle("verification-check-form", Boolean(formState.isDirty) || isAmlFormDirty);
    };
    $[41] = amlValues;
    $[42] = defaultValues.aml;
    $[43] = formState.isDirty;
    $[44] = toggle;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== amlValues || $[47] !== formState.isDirty) {
    t24 = [formState.isDirty, amlValues];
    $[46] = amlValues;
    $[47] = formState.isDirty;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  useEffect(t23, t24);
  let t25;
  if ($[49] !== mutation || $[50] !== node.id) {
    t25 = async values => {
      const verificationChecks = [];
      if (values.kyc) {
        verificationChecks.push({
          kind: "kyc" as const,
          data: {}
        });
      }
      if (values.sentilink) {
        verificationChecks.push({
          kind: "sentilink" as const,
          data: {}
        });
      }
      if (values.neuroId) {
        verificationChecks.push({
          kind: "neuro_id" as const,
          data: {}
        });
      }
      if (values.aml.enhancedAml) {
        const adverseMediaLists = Object.entries(values.aml.adverseMediaList).filter(_temp8).map(_temp9);
        const matchKind_0 = values.aml.matchingMethod.kind === "fuzzy" ? values.aml.matchingMethod.fuzzyLevel : values.aml.matchingMethod.exactLevel;
        verificationChecks.push({
          kind: "aml" as const,
          data: {
            ofac: values.aml.ofac,
            pep: values.aml.pep,
            adverseMedia: values.aml.adverseMedia,
            adverseMediaLists: values.aml.adverseMedia ? adverseMediaLists : [],
            matchKind: matchKind_0,
            continuousMonitoring: values.aml.continuousMonitoring
          }
        });
      }
      if (values.kyb) {
        verificationChecks.push({
          kind: "kyb" as const,
          data: {
            einOnly: values.einOnly
          }
        });
      }
      if (values.businessAml) {
        verificationChecks.push({
          kind: "business_aml" as const,
          data: {}
        });
      }
      if (values.baselayer) {
        verificationChecks.push({
          kind: "base_layer" as const,
          data: {
            einOnly: values.baselayerEinOnly
          }
        });
      }
      ;
      try {
        const operation = {
          kind: "update_node",
          nodeData: {
            kind: "verification_check",
            verificationChecks
          },
          nodeId: node.id
        };
        await mutation.mutateAsync(operation);
      } catch (t26) {
        const error = t26;
        console.error("Error updating playbook session:", error);
      }
    };
    $[49] = mutation;
    $[50] = node.id;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const handleFormSubmit = t25;
  let t26;
  if ($[52] !== config.verificationChecks) {
    t26 = config.verificationChecks.find(_temp10);
    $[52] = config.verificationChecks;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const kyb = t26;
  let t27;
  if ($[54] !== config.verificationChecks) {
    t27 = config.verificationChecks.find(_temp11);
    $[54] = config.verificationChecks;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const kyc = t27;
  let t28;
  if ($[56] !== config.verificationChecks) {
    t28 = config.verificationChecks.find(_temp12);
    $[56] = config.verificationChecks;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const aml = t28;
  let t29;
  if ($[58] !== config.verificationChecks) {
    t29 = config.verificationChecks.find(_temp13);
    $[58] = config.verificationChecks;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const businessAml = t29;
  let t30;
  if ($[60] !== config.verificationChecks) {
    t30 = config.verificationChecks.find(_temp14);
    $[60] = config.verificationChecks;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  const neuro = t30;
  let t31;
  if ($[62] !== config.verificationChecks) {
    t31 = config.verificationChecks.find(_temp15);
    $[62] = config.verificationChecks;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  const sentilink = t31;
  let t32;
  if ($[64] !== config.verificationChecks) {
    t32 = config.verificationChecks.find(_temp16);
    $[64] = config.verificationChecks;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const baselayer = t32;
  let t33;
  if ($[66] !== handleFormSubmit || $[67] !== handleSubmit) {
    t33 = handleSubmit(handleFormSubmit);
    $[66] = handleFormSubmit;
    $[67] = handleSubmit;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== canEdit || $[70] !== kyc || $[71] !== t) {
    t34 = !!kyc && <Section title={t(_temp17)} Icon={IcoIdCard24} testID="kyc-section"><KycToggle config={kyc} canEdit={canEdit} /></Section>;
    $[69] = canEdit;
    $[70] = kyc;
    $[71] = t;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== aml || $[74] !== canEdit || $[75] !== t) {
    t35 = !!aml && <Section title={t(_temp18)} Icon={IcoShield24} testID="aml-section"><UserAmlForm disabled={!canEdit} /></Section>;
    $[73] = aml;
    $[74] = canEdit;
    $[75] = t;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== canEdit || $[78] !== neuro || $[79] !== sentilink || $[80] !== t) {
    t36 = (!!sentilink || !!neuro) && <Section title={t(_temp19)} Icon={IcoBolt24} testID="fraud-section">{!!sentilink && <SentilinkToggle config={sentilink} canEdit={canEdit} />}{!!neuro && <NeuroToggle config={neuro} canEdit={canEdit} />}</Section>;
    $[77] = canEdit;
    $[78] = neuro;
    $[79] = sentilink;
    $[80] = t;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== canEdit || $[83] !== kyb || $[84] !== t) {
    t37 = !!kyb && <Section title={t(_temp20)} Icon={IcoIdCard24} testID="kyb-section"><KybToggle config={kyb} canEdit={canEdit} /></Section>;
    $[82] = canEdit;
    $[83] = kyb;
    $[84] = t;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== baselayer || $[87] !== canEdit || $[88] !== t) {
    t38 = !!baselayer && <Section title={t(_temp21)} Icon={IcoIdCard24} testID="base-layer-section"><BaseLayerToggle config={baselayer} canEdit={canEdit} /></Section>;
    $[86] = baselayer;
    $[87] = canEdit;
    $[88] = t;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== businessAml || $[91] !== canEdit || $[92] !== t) {
    t39 = !!businessAml && <Section title={t(_temp22)} Icon={IcoShield24} testID="business-aml-section"><BusinessAmlToggle config={businessAml} canEdit={canEdit} /></Section>;
    $[90] = businessAml;
    $[91] = canEdit;
    $[92] = t;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== t34 || $[95] !== t35 || $[96] !== t36 || $[97] !== t37 || $[98] !== t38 || $[99] !== t39) {
    t40 = <div className="flex flex-col gap-6"><div className="flex flex-col gap-4 mb-8">{t34}{t35}{t36}{t37}{t38}{t39}</div></div>;
    $[94] = t34;
    $[95] = t35;
    $[96] = t36;
    $[97] = t37;
    $[98] = t38;
    $[99] = t39;
    $[100] = t40;
  } else {
    t40 = $[100];
  }
  let t41;
  if ($[101] !== t33 || $[102] !== t40) {
    t41 = <form id="verification-check-form" onSubmit={t33}>{t40}</form>;
    $[101] = t33;
    $[102] = t40;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  if ($[104] !== methods || $[105] !== t41) {
    t42 = <FormProvider {...methods}>{t41}</FormProvider>;
    $[104] = methods;
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  return t42;
};
const useGetDisabledReasonText = reason => {
  const $ = _c(19);
  const {
    t
  } = useTranslation("playbook-builder");
  if (!reason) {
    return;
  }
  if (reason.kind === "missing_required_data") {
    let t0;
    if ($[0] !== reason.description || $[1] !== t) {
      t0 = t(_temp23, {
        data: reason.description
      });
      $[0] = reason.description;
      $[1] = t;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== t) {
    t0 = t(_temp24);
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== t) {
    t1 = t(_temp25);
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t) {
    t2 = t(_temp26);
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t) {
    t3 = t(_temp27);
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t(_temp28);
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      cannot_disable_bo_kyc_in_kyb: t0,
      unsupported_graph_subject: t1,
      not_enabled_for_tenant: t2,
      internationl_residents_not_supported: t3,
      must_run_full_kyb: t4
    };
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const disabledReasonText = t5;
  return disabledReasonText[reason.kind];
};
const KycToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp29);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp30);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="kyc" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const SentilinkToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp31);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp32);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="sentilink" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const NeuroToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp33);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp34);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="neuroId" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const KybToggle = t0 => {
  const $ = _c(21);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["kyb"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [runKyb] = useWatch(t2);
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp35);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp36);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== canEdit || $[8] !== t3 || $[9] !== t4 || $[10] !== tooltipText) {
    t5 = <TooltipToggle name="kyb" canEdit={canEdit} label={t3} hint={t4} disabledTooltipText={tooltipText} />;
    $[7] = canEdit;
    $[8] = t3;
    $[9] = t4;
    $[10] = tooltipText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== canEdit || $[13] !== config.canRunFullKyb || $[14] !== control || $[15] !== runKyb || $[16] !== t) {
    t6 = runKyb ? <><Divider variant="secondary" /><Controller name="einOnly" control={control} render={t7 => {
        const {
          field: t8
        } = t7;
        const {
          value,
          onChange,
          onBlur
        } = t8;
        return <div className="flex flex-col gap-3"><Tooltip disabled={config.canRunFullKyb} content={t(_temp37)}><Radio label={t(_temp38)} value="full" checked={!value} onChange={() => onChange(false)} onBlur={onBlur} disabled={!canEdit || !config.canRunFullKyb} /></Tooltip><Radio hint={t(_temp39)} label={t(_temp40)} value="ein" checked={value} onChange={() => onChange(true)} onBlur={onBlur} disabled={!canEdit} /></div>;
      }} /></> : null;
    $[12] = canEdit;
    $[13] = config.canRunFullKyb;
    $[14] = control;
    $[15] = runKyb;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
const BusinessAmlToggle = t0 => {
  const $ = _c(9);
  const {
    canEdit,
    config
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp41);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp42);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="businessAml" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const BaseLayerToggle = t0 => {
  const $ = _c(21);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["baselayer"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [runBaseLayer] = useWatch(t2);
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp43);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp44);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== canEdit || $[8] !== t3 || $[9] !== t4 || $[10] !== tooltipText) {
    t5 = <TooltipToggle name="baselayer" canEdit={canEdit} label={t3} hint={t4} disabledTooltipText={tooltipText} />;
    $[7] = canEdit;
    $[8] = t3;
    $[9] = t4;
    $[10] = tooltipText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== canEdit || $[13] !== config.canRunFullKyb || $[14] !== control || $[15] !== runBaseLayer || $[16] !== t) {
    t6 = runBaseLayer ? <><Divider variant="secondary" /><Controller name="baselayerEinOnly" control={control} render={t7 => {
        const {
          field: t8
        } = t7;
        const {
          value,
          onChange,
          onBlur
        } = t8;
        return <div className="flex flex-col gap-3"><Tooltip disabled={config.canRunFullKyb} content={t(_temp45)}><Radio label={t(_temp46)} value="full" checked={!value} onChange={() => onChange(false)} onBlur={onBlur} disabled={!canEdit || !config.canRunFullKyb} /></Tooltip><Radio hint={t(_temp47)} label={t(_temp48)} value="ein" checked={value} onChange={() => onChange(true)} onBlur={onBlur} disabled={!canEdit} /></div>;
      }} /></> : null;
    $[12] = canEdit;
    $[13] = config.canRunFullKyb;
    $[14] = control;
    $[15] = runBaseLayer;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
export default ({
  node,
  canEdit,
  mutation
}: Omit<VerificationCheckFormProps, 'config'>) => {
  const config = useGetNodeConfigContext(node.id, 'verification_check');
  if (!config) {
    return <div className="flex flex-col gap-4">
        <Shimmer width="100%" height="103px" />
        <Shimmer width="100%" height="103px" />
      </div>;
  }
  return <VerificationCheckFormInternal node={node} canEdit={canEdit} config={config} mutation={mutation} />;
};
function _temp(check) {
  return check.kind === "aml";
}
function _temp2(check_0) {
  return check_0.kind === "kyb";
}
function _temp3(check_1) {
  return check_1.kind === "base_layer";
}
function _temp4(check_2) {
  return check_2.kind === "kyc";
}
function _temp5(check_3) {
  return check_3.kind === "sentilink";
}
function _temp6(check_4) {
  return check_4.kind === "neuro_id";
}
function _temp7(check_5) {
  return check_5.kind === "business_aml";
}
function _temp8(t0) {
  const [, enabled] = t0;
  return enabled;
}
function _temp9(t0) {
  const [listName] = t0;
  return listName as AdverseMediaListKind;
}
function _temp10(check_6) {
  return check_6.kind === "kyb";
}
function _temp11(check_7) {
  return check_7.kind === "kyc";
}
function _temp12(check_8) {
  return check_8.kind === "aml";
}
function _temp13(check_9) {
  return check_9.kind === "business_aml";
}
function _temp14(check_10) {
  return check_10.kind === "neuro_id";
}
function _temp15(check_11) {
  return check_11.kind === "sentilink";
}
function _temp16(check_12) {
  return check_12.kind === "base_layer";
}
function _temp17($) {
  return $.verificationChecks.kyc.title;
}
function _temp18($_0) {
  return $_0.verificationChecks.aml.title;
}
function _temp19($_1) {
  return $_1.verificationChecks.fraud.title;
}
function _temp20($_2) {
  return $_2.verificationChecks.kyb.run.title;
}
function _temp21($_3) {
  return $_3.verificationChecks.baselayer.run.title;
}
function _temp22($_4) {
  return $_4.verificationChecks.businessAml.title;
}
function _temp23($) {
  return $.verificationChecks.general.missingRequiredData;
}
function _temp24($_0) {
  return $_0.verificationChecks.kyc.disabledInBoSubgraph;
}
function _temp25($_1) {
  return $_1.verificationChecks.general.unsupportedGraphSubject;
}
function _temp26($_2) {
  return $_2.verificationChecks.general.notEnabledForTenant;
}
function _temp27($_3) {
  return $_3.verificationChecks.general.internationalCountriesUnsupported;
}
function _temp28($_4) {
  return $_4.verificationChecks.disabled.mustRunFullKyb;
}
function _temp29($) {
  return $.verificationChecks.kyc.label;
}
function _temp30($_0) {
  return $_0.verificationChecks.kyc.hint;
}
function _temp31($) {
  return $.verificationChecks.fraud.sentilink.label;
}
function _temp32($_0) {
  return $_0.verificationChecks.fraud.sentilink.hint;
}
function _temp33($) {
  return $.verificationChecks.fraud.neuroid.label;
}
function _temp34($_0) {
  return $_0.verificationChecks.fraud.neuroid.hint;
}
function _temp35($) {
  return $.verificationChecks.kyb.run.label;
}
function _temp36($_0) {
  return $_0.verificationChecks.kyb.run.hint;
}
function _temp37($_1) {
  return $_1.verificationChecks.kyb.full.disabled;
}
function _temp38($_2) {
  return $_2.verificationChecks.kyb.full.label;
}
function _temp39($_3) {
  return $_3.verificationChecks.kyb.ein.hint;
}
function _temp40($_4) {
  return $_4.verificationChecks.kyb.ein.label;
}
function _temp41($) {
  return $.verificationChecks.businessAml.label;
}
function _temp42($_0) {
  return $_0.verificationChecks.businessAml.hint;
}
function _temp43($) {
  return $.verificationChecks.baselayer.run.label;
}
function _temp44($_0) {
  return $_0.verificationChecks.baselayer.run.hint;
}
function _temp45($_1) {
  return $_1.verificationChecks.baselayer.full.disabled;
}
function _temp46($_2) {
  return $_2.verificationChecks.baselayer.full.label;
}
function _temp47($_3) {
  return $_3.verificationChecks.baselayer.ein.hint;
}
function _temp48($_4) {
  return $_4.verificationChecks.baselayer.ein.label;
}